import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { LoadingState } from '@/components/States';
import { ChevronLeft, Paperclip, History as HistoryIcon, FileText, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { maskPhone, formatDate } from '@/lib/format';
import TicketHeader from '@/components/ticket/TicketHeader';
import AttachmentsTab from '@/components/ticket/AttachmentsTab';
import HistoryTab from '@/components/ticket/HistoryTab';

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="text-sm text-slate-900 mt-1">{value || '-'}</div>
    </div>
  );
}

const EDITABLE_FIELDS = ['status', 'priority', 'assignedToId', 'responsibleAction', 'conclusion'];

export default function TicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole('SUPER_ADMIN');
  const companyId = searchParams.get('companyId');

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});

  const canEdit = hasRole('SUPER_ADMIN', 'ADMIN', 'SUPERVISOR', 'ATTENDANT');

  const loadTicket = useCallback(async () => {
    try {
      const params = isSuperAdmin && companyId ? { companyId } : {};
      const { data } = await api.get(`/tickets/${id}`, { params });
      setTicket(data);
      setEdit({
        status: data.status,
        priority: data.priority,
        assignedToId: data.assignedToId || '',
        responsibleAction: data.responsibleAction || '',
        conclusion: data.conclusion || '',
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load ticket:', err);
      toast.error('Ticket não encontrado');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, isSuperAdmin, companyId]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/tickets/${id}/history`);
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load history:', err);
      setHistory([]);
    }
  }, [id]);

  const loadAttachments = useCallback(async () => {
    try {
      const { data } = await api.get(`/tickets/${id}/attachments`);
      setAttachments(Array.isArray(data) ? data : []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load attachments:', err);
      setAttachments([]);
    }
  }, [id]);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get('/users', { params: { pageSize: 100 } });
      setUsers(data.items);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.debug('Could not load users (non-critical):', err?.message || err);
    }
  }, []);

  useEffect(() => {
    loadTicket();
    loadHistory();
    loadAttachments();
    loadUsers();
  }, [loadTicket, loadHistory, loadAttachments, loadUsers]);

  const saveChanges = useCallback(async () => {
    if (!ticket) return;
    setSaving(true);
    try {
      const payload = {};
      EDITABLE_FIELDS.forEach((k) => {
        if (edit[k] !== undefined && edit[k] !== (ticket[k] || '')) {
          payload[k] = edit[k] === '' ? null : edit[k];
        }
      });
      if (Object.keys(payload).length === 0) {
        setEditing(false);
        return;
      }
      const params = isSuperAdmin && companyId ? { companyId } : {};
      await api.patch(`/tickets/${id}`, payload, { params });
      toast.success('Ticket atualizado');
      setEditing(false);
      loadTicket();
      loadHistory();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to update ticket:', err);
      toast.error(err.response?.data?.detail || 'Erro ao atualizar ticket');
    } finally {
      setSaving(false);
    }
  }, [ticket, edit, id, loadTicket, loadHistory, isSuperAdmin, companyId]);

  if (loading) return <LoadingState rows={10} />;
  if (!ticket) return null;

  return (
    <div className="space-y-4">
      <Button variant="ghost" onClick={() => navigate('/tickets')} className="text-slate-600 -ml-2">
        <ChevronLeft className="w-4 h-4 mr-1" /> Voltar
      </Button>

      <TicketHeader
        ticket={ticket}
        canEdit={canEdit}
        editing={editing}
        saving={saving}
        edit={edit}
        setEdit={setEdit}
        users={users}
        onEdit={() => setEditing(true)}
        onCancel={() => setEditing(false)}
        onSave={saveChanges}
      />

      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info"><FileText className="w-4 h-4 mr-2" /> Informações</TabsTrigger>
          <TabsTrigger value="desc"><Activity className="w-4 h-4 mr-2" /> Descrições</TabsTrigger>
          <TabsTrigger value="att">
            <Paperclip className="w-4 h-4 mr-2" /> Anexos {attachments?.length > 0 && `(${attachments.length})`}
          </TabsTrigger>
          <TabsTrigger value="hist"><HistoryIcon className="w-4 h-4 mr-2" /> Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <Card className="border-slate-200 p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <Field label="Cliente" value={ticket.customer?.name} />
            <Field label="Produto" value={ticket.product?.name} />
            <Field label="Categoria" value={ticket.category?.name} />
            <Field label="Responsável" value={ticket.assignedTo?.name} />
            <Field label="Contato" value={ticket.contactName} />
            <Field label="Telefone" value={maskPhone(ticket.contactPhone)} />
            <Field label="Cidade/UF" value={ticket.customer?.city ? `${ticket.customer.city}/${ticket.customer.state || '-'}` : '-'} />
            <Field label="Motivo" value={ticket.reason} />
            <Field label="Aberto em" value={formatDate(ticket.openedAt, true)} />
          </Card>
        </TabsContent>

        <TabsContent value="desc">
          <Card className="border-slate-200 p-5 space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Problema relatado</Label>
              <p className="text-sm text-slate-900 mt-1 whitespace-pre-wrap">{ticket.problemDescription || '-'}</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Ação do responsável</Label>
              {editing ? (
                <Textarea rows={4} value={edit.responsibleAction} onChange={(e) => setEdit({ ...edit, responsibleAction: e.target.value })} />
              ) : (
                <p className="text-sm text-slate-900 mt-1 whitespace-pre-wrap">{ticket.responsibleAction || '-'}</p>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Conclusão</Label>
              {editing ? (
                <Textarea rows={3} value={edit.conclusion} onChange={(e) => setEdit({ ...edit, conclusion: e.target.value })} />
              ) : (
                <p className="text-sm text-slate-900 mt-1 whitespace-pre-wrap">{ticket.conclusion || '-'}</p>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="att">
          <AttachmentsTab
            ticketId={id}
            attachments={attachments}
            onRefresh={loadAttachments}
            onAfterUpload={loadHistory}
          />
        </TabsContent>

        <TabsContent value="hist">
          <HistoryTab ticketId={id} history={history} onRefresh={loadHistory} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
